import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import '../../styles/pages/device-list.css';
import '../../styles/components/release-notes-modal.css';
import { /*ChevronDown*/ColumnsIcon ,PlusIcon, Search, /*Upload*/Grid3X3, MapPin, X } from 'lucide-react';
import DeviceTableView from './DeviceTableView';
import Devicemapview from './Devicemapview';
import DeleteDeviceModal from '../../components/modals/DeleteDeviceModal';
import { DevicesContext } from '../../contexts/DevicesContext';
import AQILearningModal from '../../components/modals/AQILearningModal';
import EditColumnsModal from '../../components/modals/EditColumnsModal';
// import { useAuth } from '../../hooks/useAuth';
import DeviceCard from './DeviceCard';
import { ParameterDtoPreferred } from '../../services/deviceApi';
import { UserSelectedContext } from '../../contexts/UserSelectedContext';
import ReleaseNotesModal from '../../components/modals/ReleaseNotesModal';
import {
  ReleaseNote,
  getLatestReleaseNotes,
  isReleaseNoteDismissed,
  dismissReleaseNote,
  isWithin48Hours,
} from '../../services/releaseNotesApi';
const DeviceViewPage: React.FC = () => {

  const defaultColumns = ['PM10','PM2.5','CO₂','VOC','Temperature','Humidity']

  const navigate = useNavigate();
  // const { user } = useAuth();
  // const [viewMode, setViewMode] = useState<'table' | 'map'>('table');
  // Delete device modal
  const [isDeleteDeviceModalOpen, setIsDeleteDeviceModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingSuccess, setIsDeletingSuccess] = useState(false);
  const [deviceIdToDelete, setDeviceIdToDelete] = useState<number | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [isAQILearningModalOpen, setIsAQILearningModalOpen] = useState(false);
  const [isEditColumnsModalOpen, setIsEditColumnsModalOpen] = useState(false);
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }
  const { 
    devicesForDashboard: devices, 
    parametersForDashboard: parameters,
    loadingLiveData,
    deleteDevice,
    demoDeviceId,
    demoUserId
    // lastRefreshTime,
  } = devicesContext;

  /*User Selected Context*/
  const userSelectedContext = useContext(UserSelectedContext);
  if (!userSelectedContext) {
    throw new Error('useUserSelected must be used within a UserSelectedProvider');
  }

  // const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const [selectedColumns, setSelectedColumns] = useState<number[]>([]);
  const [filteredSelectedColumnsForDashboard, setFilteredSelectedColumnsForDashboard] = useState<ParameterDtoPreferred[] | []>([]);
  
  // Release notes state
  const [releaseNote, setReleaseNote] = useState<ReleaseNote | null>(null);
  const [showReleaseNotesBanner, setShowReleaseNotesBanner] = useState(false);
  const [isReleaseNotesModalOpen, setIsReleaseNotesModalOpen] = useState(false);

  // Fetch release notes once on mount
  useEffect(() => {
    if (process.env.REACT_APP_FEATURE_RELEASE_NOTES !== 'true') return;
    const fetchReleaseNotes = async () => {
      const note = await getLatestReleaseNotes();
      if (note && isWithin48Hours(note.releaseDate) && !isReleaseNoteDismissed(note.id)) {
        setReleaseNote(note);
        setShowReleaseNotesBanner(true);
      }
    };
    fetchReleaseNotes();
  }, []);

  const handleDismissBanner = useCallback(() => {
    if (releaseNote) {
      dismissReleaseNote(releaseNote.id);
    }
    setShowReleaseNotesBanner(false);
  }, [releaseNote]);

  const handleViewWhatsNew = useCallback(() => {
    setIsReleaseNotesModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsReleaseNotesModalOpen(false);
  }, []);

  const handleConfirmModal = useCallback(() => {
    setIsReleaseNotesModalOpen(false);
    handleDismissBanner();
  }, [handleDismissBanner]);

  const handleAddDevice = useCallback(() => {
    navigate('/add-device');
    //  navigate('/manually-add-device');
  }, []);

  const handleDeviceClick = useCallback((deviceId: number) => {
    navigate(`/devices/${deviceId}`);
  }, []);

  // handle delete device modal
  const handleDelteDeviceBeforePopupConfirm = (id: number) => {
    setDeviceIdToDelete(id);
    setIsDeleteDeviceModalOpen(true);
  }

  // Handle delete device function
   const handleDeleteDevice = async () => {
    console.log('handleDeleteDevice', deviceIdToDelete);
    try {
      setIsDeleting(true);
      const response = await deleteDevice(deviceIdToDelete?.toString() as string);
      if (response.success) {
        setIsDeletingSuccess(true);
        setIsDeleting(false);
        setTimeout(() => {
          navigate('/devices');
        }, 1500);
      } else {
        console.error('Error deleting device', response);
        setIsDeleting(false);
      }
    } catch (error) {
      console.error('Error deleting device', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // const handleExportDropdownClick = (e: React.MouseEvent<HTMLButtonElement>) => {
  //   e.stopPropagation();
  //   setShowExportDropdown(prev => !prev);
  // };

  useEffect(() => {
    const defaultSelectedColumns = JSON.parse(localStorage.getItem('userSelectedColumnsForDashboard') || '[]');
    if (defaultSelectedColumns.length > 0) {
      setSelectedColumns(defaultSelectedColumns);
      setFilteredSelectedColumnsForDashboard(parameters.filter(parameter => defaultSelectedColumns.includes(parameter.driverID)));
    } else {
      setSelectedColumns(parameters.filter(parameter => defaultColumns.includes(parameter.parameterName)).map(parameter => parameter.driverID));
      setFilteredSelectedColumnsForDashboard(parameters.filter(parameter => defaultColumns.includes(parameter.parameterName)));
    }
  }, [parameters]);

  const handleSelectColumn = (driverID: number) => {
    setSelectedColumns(prev => prev.includes(driverID) ? prev.filter(id => id !== driverID) : [...prev, driverID]);
  };

  const handleSaveColumns = () => {
    setIsEditColumnsModalOpen(false);
    const selectedColumnsForDashboard = parameters.filter(parameter => selectedColumns.includes(parameter.driverID));
    setFilteredSelectedColumnsForDashboard(selectedColumnsForDashboard);
    localStorage.setItem('userSelectedColumnsForDashboard', JSON.stringify(selectedColumnsForDashboard.map(parameter => parameter.driverID)));
  };

  const handleSelectAllColumns = () => {
    setSelectedColumns(prev => prev.length === parameters.length ? [] : parameters.map(parameter => parameter.driverID));
  };


  useEffect(() => {
    /* Handle click outside */
    // const handleClickOutside = (e: MouseEvent) => {
    //   if (
    //     e.target instanceof HTMLElement &&
    //     !e.target.classList.contains('export-dropdown')
    //   ) {
    //     setShowExportDropdown(false);
    //   }
      // if (
      //   e.target instanceof HTMLElement &&
      //   !e.target.classList.contains('edit-columns-dropdown-content')
      // ) {
      //   setShowEditColumnsDropdown(false);
      // }
  //  };

   /* Handle resize */
   const handleResize = () => {
      setScreenSize(window.innerWidth);
   };

  //  document.addEventListener("click", handleClickOutside);
   window.addEventListener("resize", handleResize);
   return () => {
    // document.removeEventListener("click", handleClickOutside);
    window.removeEventListener("resize", handleResize);
   };
  }, []);
  
  // const handleExport = async (fileType: string) => {
  //   try {
  //     console.log('Exporting device data:', { fileType });
  //     await exportDashboardData(fileType);
  //     setShowExportDropdown(false);
  //   } catch (error) {
  //     console.error('Export failed:', error);
  //   }
  // };

  /* Filter devices based on search term and filters */
  const filteredDevices = useMemo(() => (devices || []).filter(device => {
    // Apply search filter
    if (searchQuery && !(device.deviceName?.toLowerCase().includes(searchQuery.toLowerCase()))) {
      return false;
    }

    return true;
  }), [devices, searchQuery]);


  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view") as "table" | "map";
  const viewMode = viewParam ?? "table";

  useEffect(() => {
    if (!viewParam) {
      setSearchParams({ view: viewMode }, { replace: true });
    }
  }, [])
  

  // sync tab from URL at page load
  // useEffect(() => {
  //   if (viewParam && viewParam !== viewMode) {
  //     setViewMode(viewParam)
  //   }
  // }, [searchParams]);
  
  const toggleView = (view: "table" | "map") => {
    setSearchParams({ view }, { replace: true });
  }


  /* Get greeting based on the time of the day */
  // const getGreeting = () => {
  //   const hours = new Date().getHours();
  //   if (hours < 12) return 'Good Morning';
  //   if (hours < 18) return 'Good Afternoon';
  //   if (hours < 24) return 'Good Night';
  //   return 'Good Day';
  // };

  return (
    <DashboardLayout>
      <div className="device-list-container">
        {/* Header */}
        <div className="device-list-header zoomOutLevel">
          {/* <div className="flex items-center justify-between md:w-auto w-full"> */}
          {/* Desktop View title and subtitle */}
          {screenSize > 768 ? (
            <div className="min-w-[35%]">
              <h1 className="device-title">Devices</h1>
              <p className="device-subtitle ml-0">
                Monitor your indoor air quality devices in real-time.
              </p>
            </div>
          ) : (
            <div className="w-full flex items-center justify-between">
              <img
                src={`${process.env.PUBLIC_URL}/ISTLogo/ISTLOGO.gif`}
                alt="SmartEco"
                className="w-28 h-10"
              />
              <img
                src={`${process.env.PUBLIC_URL}/SVGIcons/SmartecoLogo.svg`}
                alt="SmartEco"
                className="w-36 h-10"
              />
            </div>
          )}

          {/* <div className="flex items-center gap-4 md:hidden">
               <div className='notification-bell'>
                <Bell className="w-6 h-6 text-white" />
                <p className='notification-badge'>1</p>
              </div>
              {/* <div className="aqi-info" onClick={() => setIsAQILearningModalOpen(true)} style={{ cursor: 'pointer' }}>
                <img src={`${process.env.PUBLIC_URL}/DevicePageIcons/Info.svg`} alt="Info" />
              </div> 
            </div>*/}
          {/* </div> */}
          <div className="flex justify-end items-center gap-4 w-full">
            {viewMode === 'table' && (
              <div className="search-bar-container lg:min-w-[400px] flex-1">
                <input
                  type="text"
                  placeholder="Search by Device Name"
                  className="search-bar-input"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                <div className="search-bar-icon">
                  <Search className="w-6 h-6 text-white" />
                </div>
              </div>
            )}

            {(viewMode === 'table' || screenSize > 768) && (
              <button onClick={handleAddDevice} className="add-device-button">
                {screenSize > 768 ? (
                  '+ Device'
                ) : (
                  <PlusIcon className="w-5 h-5" />
                )}
              </button>
            )}
            {/* <div className="export-dropdown-container">
                <button className={`export-data-button ${showExportDropdown ? 'active' : ''}`} onClick={(e) => handleExportDropdownClick(e)}>
                  {screenSize > 768 ? (
                    <>
                      <p>Export Data</p>
                      <ChevronDown className={`w-5 h-5 ml-2 ${showExportDropdown ? 'rotate-180' : ''}`} />
                    </>
                  ) : (
                    <Upload className="w-6 h-6 text-[var(--color-sub-title-text)]" />
                  )}
                </button>
                {showExportDropdown && (
                  <div className="export-dropdown">
                    {ExportDataTypes.filter(type => type.value !== 'pdf' && type.value !== 'png').map((type) => (
                      <div key={type.value} className="export-dropdown-item" onClick={() => handleExport(type.value)}>
                        <img src={`${process.env.PUBLIC_URL}/SVGIcons/${type.label}.svg`} alt={type.label} />
                        <p>{type.label}</p>
                      </div>
                    ))}
                  </div>
                )}
            </div> */}
          </div>
        </div>

        {/* Release Notes Banner */}
        {showReleaseNotesBanner && releaseNote && (
          <div className="rn-banner zoomOutLevel">
            <div className="rn-banner-content">
              <p className="rn-banner-title">New SmartEco Enhancements Available</p>
              <p className="rn-banner-subtitle">{releaseNote.title}</p>
            </div>
            <div className="rn-banner-actions">
              <button className="rn-banner-view-btn" onClick={handleViewWhatsNew}>
                View What&apos;s New
              </button>
              <button className="rn-banner-close" onClick={handleDismissBanner} aria-label="Dismiss">
                <X size={18} />
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between md:px-0 px-6 zoomOutLevel">
          {/* View Toggle */}
          <div className="view-toggle">
            <button
              onClick={() => toggleView('table')}
              className={`toggle-button ${viewMode === 'table' ? 'active' : ''}`}
            >
              <Grid3X3 className="w-4 h-4" />
              Table View
            </button>
            <button
              onClick={() => toggleView('map')}
              className={`toggle-button ${viewMode === 'map' ? 'active' : ''}`}
            >
              <MapPin className="w-4 h-4" />
              Map View
            </button>
          </div>

          <div className="md:flex items-center gap-4 hidden">
            {/* Add Columns Button - Only show in table view */}
            {viewMode === 'table' && (
              <button
                onClick={() => setIsEditColumnsModalOpen(true)}
                className="toggle-button"
              >
                <ColumnsIcon className="w-4 h-4" />
                Add Columns
              </button>
            )}
            {/* Auto-refresh Status */}
            {/* <div className="text-sm text-gray-600">
              <span>
                Last Refreshed At: {lastRefreshTime != null ? lastRefreshTime.toLocaleString('en-US', options) : 'N/A'}
              </span>
            </div> */}
          </div>
        </div>

        <div className="device-dashboard-container">
          {/* Content */}
          {loadingLiveData && (!devices || devices.length === 0) ? (
            <div className="h-[50vh] loading-container">
              <div className="h-8 w-8 border-b-2 border-[var(--gradient-button-left-color)] mb-4 animate-spin rounded-full"></div>
              <p className="text-base text-[var(--color-sub-title-text)]">
                Loading live data...
              </p>
            </div>
          ) : viewMode === 'table' ? (
            screenSize > 860 ? (
              <DeviceTableView
                devices={filteredDevices}
                parameters={filteredSelectedColumnsForDashboard}
                onDeviceClick={handleDeviceClick}
                searchTerm={searchQuery}
                handleDeleteDevice={handleDelteDeviceBeforePopupConfirm}
                demoDeviceId={demoDeviceId}
                demoUserId={demoUserId}
              />
            ) : (
              <DeviceCard
                devices={filteredDevices}
                parameters={parameters}
                onDeviceClick={handleDeviceClick}
                handleDeleteDevice={handleDelteDeviceBeforePopupConfirm}
                demoDeviceId={demoDeviceId}
                demoUserId={demoUserId}
              />
            )
          ) : (
            <Devicemapview devices={devices} parameters={parameters} />
          )}
        </div>
      </div>

      <AQILearningModal
        isOpen={isAQILearningModalOpen}
        onClose={() => setIsAQILearningModalOpen(false)}
      />

      <EditColumnsModal
        isOpen={isEditColumnsModalOpen}
        onClose={() => setIsEditColumnsModalOpen(false)}
        parameters={parameters}
        selectedColumns={selectedColumns}
        onSave={handleSaveColumns}
        onSelectColumn={handleSelectColumn}
        onSelectAllColumns={handleSelectAllColumns}
      />

      <DeleteDeviceModal
        isOpen={isDeleteDeviceModalOpen}
        onClose={() => setIsDeleteDeviceModalOpen(false)}
        onConfirm={() => handleDeleteDevice()}
        isDeleting={isDeleting}
        isDeletingSuccess={isDeletingSuccess}
      />

      {releaseNote && (
        <ReleaseNotesModal
          isOpen={isReleaseNotesModalOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmModal}
          releaseNote={releaseNote}
        />
      )}
    </DashboardLayout>
  );
};

export default DeviceViewPage;
